import  { useState } from 'react'
import useCurrentTeacher from '../../../../hooks/useCurrentTeacher'

const Subjects = () => {
  const { teacher, fetchCurrentTeacher } = useCurrentTeacher()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentSubject, setCurrentSubject] = useState({ name: '', certification: false })
  const [originalName, setOriginalName] = useState('') // Track original name for edit

  if (!teacher) return null

  // Open modal for edit
  const handleEdit = (subject) => {
    setCurrentSubject(subject)
    setOriginalName(subject.name)
    setIsModalOpen(true)
  }

  // Open modal for add
  const handleAdd = () => {
    setCurrentSubject({ name: '', certification: false })
    setOriginalName('')
    setIsModalOpen(true)
  }

  // Delete subject
  const handleDelete = async (subjectName) => {
    if (!window.confirm(`Are you sure you want to delete ${subjectName}?`)) return

    try {
      const updatedSubjects = teacher.subjects.filter(s => s.name !== subjectName)
      const formData = new FormData()
      formData.append('subjects', JSON.stringify(updatedSubjects))

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/teacher/${teacher.id}/`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: formData
      })

      if (res.ok) {
        alert('Subject deleted successfully!')
        fetchCurrentTeacher()
      } else {
        const data = await res.json()
        alert(data.msg || 'Failed to delete subject')
      }
    } catch (err) {
      console.error(err)
      alert('Error deleting subject')
    }
  }

  // Save (add or update)
  const handleSave = async () => {
    try {
      let updatedSubjects
      if (originalName && teacher.subjects.some(s => s.name === originalName)) {
        // Update existing based on original name
        updatedSubjects = teacher.subjects.map(s =>
          s.name === originalName ? currentSubject : s
        )
      } else {
        // Add new
        updatedSubjects = [...teacher.subjects, currentSubject]
      }

      const formData = new FormData()
      formData.append('subjects', JSON.stringify(updatedSubjects))

      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/teacher/${teacher.id}/`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: formData
      })

      if (res.ok) {
        alert('Subject saved successfully!')
        fetchCurrentTeacher()
        setIsModalOpen(false)
      } else {
        const data = await res.json()
        alert(data.msg || 'Failed to save subject')
      }
    } catch (err) {
      console.error(err)
      alert('Error saving subject')
    }
  }

  return (
<div className="bg-white rounded-2xl p-2 md:p-6">
  {/* Header */}
  <div className="flex items-center justify-between mb-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
        <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Your Subjects</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage your teaching subjects</p>
      </div>
    </div>
    
    <button
      className="w-10 h-10 bg-[#0f6abf] text-white rounded-full flex items-center justify-center hover:bg-[#0a5ca8] transition-colors"
      onClick={handleAdd}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    </button>
  </div>

  {/* Table */}
  {teacher.subjects && teacher.subjects.length > 0 ? (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="grid grid-cols-12 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
        <div className="col-span-5">Subject Name</div>
        <div className="col-span-4">Certification</div>
        <div className="col-span-3 text-right">Actions</div>
      </div>
      
      <div className="divide-y divide-gray-100">
        {teacher.subjects.map((subj, index) => (
          <div key={index} className="grid grid-cols-12 items-center px-4 py-3 hover:bg-gray-50 transition-colors">
            <div className="col-span-5">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#0f6abf] rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">{subj.name}</span>
              </div>
            </div>
            
            <div className="col-span-4">
              <span className={`text-xs px-2.5 py-1 rounded-lg ${
                subj.certification 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {subj.certification ? 'Certified' : 'Not Certified'}
              </span>
            </div>
            
            <div className="col-span-3 flex justify-end gap-2">
              <button
                className="w-8 h-8 bg-gray-100 text-gray-700 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors"
                onClick={() => handleEdit(subj)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              
              <button
                className="w-8 h-8 bg-red-50 text-red-500 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors"
                onClick={() => handleDelete(subj.name)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-700 mb-2">No subjects added</h3>
      <p className="text-sm text-gray-500">Add your first teaching subject</p>
    </div>
  )}

  {/* Modal */}
  {isModalOpen && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {originalName ? 'Edit Subject' : 'Add Subject'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {originalName ? 'Update subject details' : 'Add a new teaching subject'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject Name</label>
              <input
                type="text"
                placeholder="Enter subject name"
                value={currentSubject.name}
                onChange={(e) =>
                  setCurrentSubject({ ...currentSubject, name: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <div className="relative">
                <input
                  type="checkbox"
                  id="certification"
                  checked={currentSubject.certification}
                  onChange={(e) =>
                    setCurrentSubject({ ...currentSubject, certification: e.target.checked })
                  }
                  className="sr-only"
                />
                <div className="w-5 h-5 border-2 border-gray-300 rounded flex items-center justify-center">
                  {currentSubject.certification && (
                    <svg className="w-3 h-3 text-[#0f6abf]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <label htmlFor="certification" className="text-sm text-gray-700 cursor-pointer">
                Has certification for this subject
              </label>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
          <div className="flex gap-3">
            <button
              onClick={() => setIsModalOpen(false)}
              className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 py-2.5 bg-[#0f6abf] text-white font-medium rounded-xl hover:bg-[#0a5ca8] transition-colors"
            >
              {originalName ? 'Update Subject' : 'Add Subject'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )}
</div>

  )
}

export default Subjects
